<template>
  <div>
    <PageAllow controller="StockPositions" action="index" />
    <Breadcrumbs :items="items" />

    <v-slide-x-transition>
      <v-sheet class="overflow-y-scroll fill-content-height">
        <v-row dense class="mx-5">
          <v-col cols="4" sm="12" md="4">
            <SearcherKeyword
              ref="position_name_and_code"
              v-model="options.keyword"
              @text="positionNameAndCodeKeyword = $event"
              :label="$t('Position.positionsKeyword')"
            />
          </v-col>
          <v-col cols="12" sm="12" md="8" align-self="center">
            <v-row dense align="center" class="search-controls">
              <v-tooltip bottom>
                <template v-slot:activator="{ on, attrs }">
                  <v-btn
                    v-bind="attrs"
                    v-on="on"
                    small
                    text
                    class="px-0 py-0 caption font-weight-bold grey--text"
                    color="primary"
                    @click.stop="onSearch()"
                  >
                    <v-icon small class="pe-1">fa-search</v-icon>
                    {{ $t("Search.search") }}
                  </v-btn>
                </template>
                <span>{{ $t("Search.searchTip") }}</span>
              </v-tooltip>
              <v-tooltip bottom>
                <template v-slot:activator="{ on, attrs }">
                  <v-btn
                    v-bind="attrs"
                    v-on="on"
                    small
                    text
                    class="caption font-weight-bold grey--text"
                    @click.stop="resetSearch()"
                  >
                    <v-icon x-small class="pe-1">fa-trash-alt</v-icon>
                    {{ $t("Search.reset") }}
                  </v-btn>
                </template>
                <span>{{ $t("Search.resetTip") }}</span>
              </v-tooltip>
              <v-divider vertical />
              <v-btn
                class="caption font-weight-bold grey--text"
                :class="
                  $vuetify.theme.dark ? 'text--lighten-1' : 'text--darken-1'
                "
                small
                text
                @click.stop="$store.commit('toggleSearchPanel')"
              >
                {{ $t("Search.pro") }}
                <v-icon small class="ps-1">{{
                  $store.state.showSearchPanel ? "fa-angle-up" : "fa-angle-down"
                }}</v-icon>
              </v-btn>
              <v-spacer />
              <v-btn
                v-if="
                  $permission.permissionAllow('StockPositions', 'import')
                "
                small
                color="print"
                dark
                @click.stop="importDialog = true"
                class="ms-1 float-end"
              >
                <v-icon x-small class="pe-1">fa-file-import</v-icon>
                {{ $t("Import.import", [$t("Position.ObjectName")]) }}
              </v-btn>
              <v-btn
                v-if="$permission.permissionAllow('StockPositions', 'add')"
                small
                color="primary"
                @click.stop="
                  currentItem = { status: 1, stop_position_flg: 1 };
                  editDialog = true;
                "
                class="ms-1"
              >
                <v-icon x-small class="pe-1">fa-plus</v-icon>
                {{ $t("CRUD.newInfo", [$t("Position.ObjectName")]) }}
              </v-btn>
            </v-row>
          </v-col>
        </v-row>
        <v-expansion-panels
          v-model="$store.getters.panels"
          multiple
          flat
          accordion
        >
          <v-expansion-panel class="transparent-background-color">
            <v-expansion-panel-content>
              <v-row dense align="center">
                <v-col
                  v-if="
                    settings &&
                    settings.stock_area_enable &&
                    stock &&
                    stock.stock_area
                  "
                  cols="12"
                  sm="6"
                  md="4"
                >
                  <SearcherStockArea
                    v-model="stockAreasSelected"
                    :stock-id="stock_id"
                  />
                </v-col>
                <v-col cols="12" sm="6" md="4">
                  <SearcherStockPositionLabels
                    v-model="positionLabelsSelected"
                    :stockId="stock_id"
                  />
                </v-col>
                <v-col cols="12" sm="6" md="4">
                  <SearcherStatus
                    v-model="options.status"
                    :label="$t('Position.status.title')"
                    :items="$consts.position.status"
                  />
                </v-col>
              </v-row>
            </v-expansion-panel-content>
          </v-expansion-panel>
        </v-expansion-panels>

        <div class="mx-4 mb-4">
          <v-data-table
            :headers="headers"
            :items="stockPositions"
            :loading="loading"
            :options.sync="options"
            :page.sync="paging.page"
            :server-items-length="paging.count"
            multi-sort
            class="elevation-2 px-4 pt-1"
            :footer-props="$t('$config.footer-props')"
          >
            <template v-slot:header.actions="{ header }">
              {{ header.text }}
              <v-icon x-small>fa-sliders-h</v-icon>
            </template>
            <template v-slot:item.area_name="{ item }">
              {{ item.stock_area ? item.stock_area.area_name : "" }}
            </template>
            <template v-slot:item.label_name="{ item }">
              {{
                item.stock_position_label
                  ? item.stock_position_label.label_name
                  : ""
              }}
            </template>
            <template v-slot:item.allow_picking="{ item }">
              <v-chip
                small
                label
                :color="
                  item.stock_position_label
                    ? item.stock_position_label.allow_picking
                    : '' | positionPickingFlgsColor
                "
                :text-color="
                  item.stock_position_label
                    ? item.stock_position_label.allow_picking
                    : '' | positionPickingFlgsTextColor
                "
              >
                {{
                  item.stock_position_label
                    ? item.stock_position_label.allow_picking
                    : "" | positionPickingFlgsText
                }}
              </v-chip>
            </template>
            <template v-slot:item.status="{ item }">
              <v-chip
                small
                label
                :color="item.status | positionStatusColor"
                :text-color="item.status | positionStatusTextColor"
              >
                {{ item.status | positionStatusText }}
              </v-chip>
            </template>
            <template v-slot:item.actions="{ item }">
              <v-tooltip
                top
                v-if="$permission.permissionAllow('StockPositions', 'edit')"
              >
                <template v-slot:activator="{ on, attrs }">
                  <v-icon
                    small
                    v-bind="attrs"
                    v-on="on"
                    color="primary"
                    class="px-1 py-4 me-1"
                    @click.stop="
                      editDialog = true;
                      currentItem = JSON.parse(JSON.stringify(item));
                    "
                    >fa-pencil-alt
                  </v-icon>
                </template>
                <span>{{ $t("CRUD.editInfo", [$t("Position.title")]) }}</span>
              </v-tooltip>
              <v-tooltip
                top
                v-if="
                  $permission.permissionAllow('StockPositions', 'delete') &&
                  item.del_position_flg
                "
              >
                <template v-slot:activator="{ on, attrs }">
                  <v-icon
                    small
                    v-bind="attrs"
                    v-on="on"
                    color="accent"
                    class="px-1 py-4 me-1"
                    @click.stop="
                      currentItem = JSON.parse(JSON.stringify(item));
                      deleteDialog = true;
                    "
                    >fa-trash-alt
                  </v-icon>
                </template>
                <span>{{ $t("CRUD.deleteInfo", [$t("Position.title")]) }}</span>
              </v-tooltip>
            </template>
          </v-data-table>
        </div>
      </v-sheet>
    </v-slide-x-transition>

    <v-dialog persistent v-model="editDialog" width="600px">
      <v-card>
        <v-card-title>
          <span class="headline">{{ $t("Position.ObjectNameInfo") }}</span>
          <v-spacer></v-spacer>
          <v-tooltip left>
            <template v-slot:activator="{ on, attrs }">
              <v-icon v-bind="attrs" v-on="on" @click="editDialog = false"
                >fa-times
              </v-icon>
            </template>
            <span>
              {{ $t("CRUD.backToList", [$t("Position.ObjectName")]) }}
            </span>
          </v-tooltip>
        </v-card-title>
        <v-card-text>
          <v-container>
            <ValidationObserver ref="observer">
              <v-form>
                <v-row>
                  <v-col cols="12">
                    <ValidationProvider
                      v-slot="{ errors }"
                      :name="$t('Position.Fields.stock_position_name')"
                      rules="required"
                    >
                      <v-text-field
                        required
                        v-model="currentItem.position_name"
                        :error-messages="
                          $resparse
                            .getErrorMsgs(serverErrors, 'number')
                            .concat(errors)
                        "
                      >
                        <template v-slot:label>
                          {{ $t("Position.Fields.stock_position_name") }}
                          <span class="red--text">*</span>
                        </template>
                      </v-text-field>
                    </ValidationProvider>
                  </v-col>
                  <v-col cols="12">
                    <ValidationProvider
                      v-slot="{ errors }"
                      :name="$t('Position.Fields.stock_position_code')"
                      rules="required"
                    >
                      <v-text-field
                        required
                        v-model="currentItem.position_code"
                        :error-messages="
                          $resparse
                            .getErrorMsgs(serverErrors, 'number')
                            .concat(errors)
                        "
                      >
                        <template v-slot:label>
                          {{ $t("Position.Fields.stock_position_code") }}
                          <span class="red--text">*</span>
                        </template>
                      </v-text-field>
                    </ValidationProvider>
                  </v-col>
                  <v-col cols="12">
                    <ValidationProvider
                      v-slot="{ errors }"
                      :name="$t('Position.Fields.stock_position_barcode')"
                      rules="required"
                    >
                      <v-text-field
                        required
                        v-model="currentItem.bar_code"
                        :error-messages="
                          $resparse
                            .getErrorMsgs(serverErrors, 'number')
                            .concat(errors)
                        "
                      >
                        <template v-slot:label>
                          {{ $t("Position.Fields.stock_position_barcode") }}
                          <span class="red--text">*</span>
                        </template>
                      </v-text-field>
                    </ValidationProvider>
                  </v-col>
                  <v-col cols="12">
                    <ValidationProvider
                      v-slot="{ errors }"
                      :name="$t('Position.Fields.position_label_name')"
                      rules="required"
                    >
                      <SearcherStockPositionLabels
                        v-model="currentPositionLabel"
                        :stockId="stock_id"
                        :error-messages="
                          $resparse
                            .getErrorMsgs(serverErrors, 'label_name')
                            .concat(errors)
                        "
                      />
                    </ValidationProvider>
                  </v-col>
                  <v-col
                    v-if="
                      settings &&
                      settings.stock_area_enable &&
                      stock &&
                      stock.stock_area
                    "
                    cols="12"
                  >
                    <SearcherStockArea
                      v-model="stockAreasBinding"
                      :stock-id="stock_id"
                    />
                  </v-col>
                  <v-col
                    v-if="
                      settings &&
                      settings.stock_roadway_setting &&
                      stock &&
                      stock.stock_roadway_setting
                    "
                    cols="12"
                  >
                    <ValidationProvider
                      v-slot="{ errors }"
                      :name="$t('stockRoadway.ObjectName')"
                      rules="required"
                    >
                      <SearcherRoadway
                        v-model="stockRoadwayBinding"
                        :stock-id="stock_id"
                        :error-messages="
                          $resparse
                            .getErrorMsgs(serverErrors, 'roadway')
                            .concat(errors)
                        "
                      />
                    </ValidationProvider>
                  </v-col>
                  <v-col cols="12">
                    <v-text-field
                      required
                      v-model="currentItem.rank"
                      type="number"
                      min="1"
                      @input="
                        currentItem.rank = $numberFormatter.onlyInt(
                          currentItem.rank
                        )
                      "
                      @blur="
                        currentItem.rank = $numberFormatter.onlyInt(
                          currentItem.rank
                        )
                      "
                    >
                      <template v-slot:label>
                        {{ $t("Position.Fields.position_rank") }}
                      </template>
                    </v-text-field>
                  </v-col>
                  <v-col cols="12">
                    <v-radio-group
                      row
                      v-model="currentItem.status"
                      :label="$t('Position.Fields.status') + $t('Global.colon')"
                      :disabled="currentItem.stop_position_flg ? false : true"
                    >
                      <v-radio
                        v-for="stat in status"
                        :key="stat.value"
                        :label="`${stat.text}`"
                        :value="stat.value"
                      ></v-radio>
                    </v-radio-group>
                  </v-col>
                </v-row>
              </v-form>
            </ValidationObserver>
          </v-container>
        </v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn color="primary" outlined @click="editDialog = false">{{
            $t("$vuetify.close")
          }}</v-btn>
          <v-btn color="accent" @click="saveEdit()">{{
            $t("CRUD.save")
          }}</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
    <DialogDelete
      v-model="deleteDialog"
      :label="$t('Position.ObjectName')"
      @confirm="
        del();
        deleteDialog = false;
      "
    />
    <ImportExcelAlert
      v-model="importDialog"
      :objName="$t('Position.ObjectName')"
      downloadTemplateUrl="download/template/admin_stock_position_import_template.xlsx"
      @import="
        excelFile = $event;
        importExcel();
      "
    />

    <v-dialog v-model="importResultDialog" max-width="960px">
      <v-card>
        <v-card-title class="mx-3">
          {{ $t("Position.import_err_title") }}
          <v-spacer></v-spacer>
          <v-tooltip left>
            <template v-slot:activator="{ on, attrs }">
              <v-icon v-bind="attrs" v-on="on" @click="importResultDialog = false">
                fa-times
              </v-icon>
            </template>
            {{ $t("$vuetify.close") }}
          </v-tooltip>
        </v-card-title>
        <v-divider />
        <v-card-text class="px-4 py-8 text-center subtitle-1">
          <v-data-table
              :headers="importResultHeaders"
              :items="importResult"
              multi-sort
              class="elevation-1 px-4 pt-2"
              disable-pagination
              hide-default-footer
              fixed-header
          >
            <template v-slot:item.error_msg="{ item }">
              <span class="red--text">
                {{ item.error_msg }}
              </span>
            </template>
          </v-data-table>
        </v-card-text>
        <v-card-actions>
          <v-spacer />
          <v-btn color="secondary" outlined @click="importResultDialog = false">
            {{ $t("$vuetify.close") }}
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </div>
</template>
<script>
import PageAllow from "@/components/PageAllow";
import Breadcrumbs from "@/components/Breadcrumbs";
import i18n from "@/i18n";
import { mapState } from "vuex";
import SearcherKeyword from "@/components/plugins/SearcherKeyword";
import SearcherStockPositionLabels from "@/components/plugins/SearcherStockPositionLabels";
import SearcherStockArea from "@/components/plugins/SearcherStockArea";
import SearcherRoadway from "@/components/plugins/SearcherStockRoadway";
import SearcherStatus from "@/components/plugins/SearcherStatus";
import StockPosition from "@/api/stock_position";
import Stocks from "@/api/stocks";
import Consts from "@/consts.js";
import DialogDelete from "@/components/plugins/DialogDelete";
import { ValidationObserver, ValidationProvider } from "vee-validate";
import ImportExcelAlert from "@/components/plugins/ImportExcelAlert.vue";

const defaultOptions = {
  stock_id: "",
  keyword: "",
  bar_code: "",
  position_label_id: "",
  stock_area_id: "",
  status: [1],
};

export default {
  components: {
    ImportExcelAlert,
    PageAllow,
    Breadcrumbs,
    SearcherKeyword,
    SearcherStockPositionLabels,
    SearcherStockArea,
    SearcherRoadway,
    SearcherStatus,
    ValidationProvider,
    ValidationObserver,
    DialogDelete,
  },

  computed: {
    status() {
      return Consts.position.status;
    },

    items() {
      return [
        {
          text: i18n.t("Nav.home"),
          disabled: false,
          href: "/home",
        },
        {
          text: i18n.t("Nav.stockManagement"),
          disabled: false,
          href: "/stocks",
        },
        {
          text: i18n.t("Nav.stocks"),
          disabled: false,
          href: "/stocks",
        },
        {
          text: i18n.t("Nav.position"),
          disabled: true,
          href: "/stocks/positions",
        },
        {
          text: this.stock && this.stock.name ? this.stock.name : "",
          disabled: false,
          accent: true,
        },
      ];
    },

    headers() {
      var headers = [];
      if (this.stock && this.stock.stock_area) {
        headers.push({
          text: i18n.t("Position.Fields.stock_area_name"),
          align: "start",
          sortable: false,
          value: "area_name",
        });
      }
      if (this.stock && this.stock.stock_roadway_setting) {
        headers.push({
          text: i18n.t("Position.Fields.roadway_code"),
          align: "start",
          sortable: false,
          value: "stock_roadway.code",
        });
      }
      headers.push(
        {
          text: i18n.t("Position.Fields.stock_position_name"),
          align: "start",
          sortable: false,
          value: "position_name",
        },
        {
          text: i18n.t("Position.Fields.stock_position_code"),
          align: "start",
          sortable: false,
          value: "position_code",
        },
        {
          text: i18n.t("Position.Fields.stock_position_barcode"),
          align: "start",
          sortable: false,
          value: "bar_code",
        },
        {
          text: i18n.t("Position.Fields.position_label_name"),
          align: "start",
          sortable: false,
          value: "label_name",
        },
        {
          text: i18n.t("Position.Fields.isPicking"),
          align: "start",
          sortable: false,
          value: "allow_picking",
        },
        {
          text: i18n.t("Position.Fields.status"),
          align: "start",
          sortable: false,
          value: "status",
        },
        {
          text: i18n.t("TableHeaders.actions"),
          align: "start",
          sortable: false,
          value: "actions",
        }
      );

      return headers;
    },
    importResultHeaders() {
      return [
        {
          text: i18n.t("Stock.Fields.stock_code"),
          align: "start",
          value: "stock_code",
        },
        {
          text: i18n.t("Stock.Fields.stock_name"),
          align: "start",
          value: "stock_name",
        },
        {
          text: i18n.t("Position.Fields.stock_position_name"),
          align: "start",
          sortable: false,
          value: "position_name",
        },
        {
          text: i18n.t("Position.Fields.stock_position_code"),
          align: "start",
          sortable: false,
          value: "position_code",
        },
        {
          text: i18n.t("Position.Fields.stock_position_barcode"),
          align: "start",
          sortable: false,
          value: "bar_code",
        },
        {
          text: i18n.t("Position.Fields.position_label_name"),
          align: "start",
          sortable: false,
          value: "position_label_name",
        },
        {
          text: i18n.t("Template.Fields.error_msg"),
          align: "start",
          sortable: false,
          value: "error_msg",
        },
      ]
    },

    // 使用对象展开运算符将此对象混入到外部对象中
    ...mapState(["loading", "settings"]),
  },

  data: () => ({
    stock_id: 0,
    stock: {},
    positionNameAndCodeKeyword: "",
    positionBarcodeKeyword: "",
    positionLabelsSelected: null,
    stockAreasSelected: null,
    stockAreasBinding: null,
    stockRoadwaySelected: null,
    stockRoadwayBinding: null,
    stockPositions: [],
    options: JSON.parse(
      sessionStorage.getItem("positions-options")
        ? sessionStorage.getItem("positions-options")
        : JSON.stringify(defaultOptions)
    ),
    paging: {},
    editDialog: false,
    currentItem: {},
    currentPositionLabel: null,
    serverErrors: [],
    deleteDialog: false,
    cards: [
      {
        title: "Favorite road trips",
        src: "https://cdn.vuetifyjs.com/images/cards/road.jpg",
        flex: 6,
      },
      {
        title: "Best airlines",
        src: "https://cdn.vuetifyjs.com/images/cards/plane.jpg",
        flex: 6,
      },
    ],
    importDialog: false,
    excelFile: null,
    importResultDialog: false,
    importResult: [],
  }),

  mounted() {
    if (this.$route.query.stock_id) {
      this.stock_id = Number(this.$route.query.stock_id);
    }
    if (this.$route.query.stock_area_id) {
      this.stockAreasSelected = { id: this.$route.query.stock_area_id };
    }

    //查仓库信息
    this.getStock(this.$route.query.stock_id);

    this.$commonUtils
      .loadOptions("positions", [
        "positionLabelsSelected",
        "stockAreasSelected",
      ])
      .then(
        function (data) {
          if (data.options) {
            this.options = data.options;
          } else this.resetSearch();

          data.selecteds.forEach((selected) => {
            switch (selected.key) {
              case "positionLabelsSelected":
                this.positionLabelsSelected = selected.value;
                break;
              case "stockAreasSelected":
                this.stockAreasSelected = selected.value;
                break;
            }
          });
        }.bind(this)
      );
  },

  methods: {
    getStock(stockId) {
      Stocks.fetchOne(stockId).then(
        function (response) {
          if (response.data.status.isOK()) {
            this.stock = response.data.stock;
          }
        }.bind(this)
      );
    },
    resetSearch() {
      this.positionLabelsSelected = null;
      this.stockAreasSelected = null;
      this.options = JSON.parse(JSON.stringify(defaultOptions));
      [this.$refs.position_name_and_code, this.$refs.position_barcode].forEach(
        (k) => {
          if (k) k.clear();
        }
      );
    },
    onSearch() {
      this.options = Object.assign({}, this.options, {
        keyword: this.positionNameAndCodeKeyword,
      });
    },
    getPostionsFromAPI() {
      StockPosition.fetch(
        Object.assign({}, this.options, { stock_id: this.stock_id })
      ).then(
        function (response) {
          if (response.data.status.isOK()) {
            this.stockPositions = response.data.stock_positions;
            this.paging = response.data.paging;
          } else if (response.data.error.code.isNoResultSet()) {
            this.options.page = 1;
          }
        }.bind(this)
      );
    },
    saveEdit() {
      this.$store.dispatch("fullscreenLoading");
      this.serverErrors = [];
      this.$refs.observer.validate().then(
        function (result) {
          if (result) {
            if (this.currentPositionLabel && this.currentPositionLabel.id) {
              this.currentItem.position_label_id = this.currentPositionLabel.id;
            }
            if (this.stockAreasBinding && this.stockAreasBinding.id) {
              this.currentItem.stock_area_id = this.stockAreasBinding.id;
            }
            this.currentItem.stock_id = this.stock_id;
            if (this.currentItem.stock_area) delete this.currentItem.stock_area;

            if (this.stockRoadwayBinding && this.stockRoadwayBinding.id) {
              this.currentItem.roadway_id = this.stockRoadwayBinding.id;
            }

            StockPosition.save(this.currentItem.id, this.currentItem)
              .then(
                function (response) {
                  if (response.data.status.isOK()) {
                    this.$emit("save", this.currentItem);
                    this.getPostionsFromAPI();
                    this.editDialog = false;
                    this.$store.dispatch("fullscreenLoadingStop");
                  } else if (
                    response.data.error &&
                    response.data.error.data &&
                    "ERR333" == response.data.error.code
                  ) {
                    this.serverErrors = response.data.error.data;
                    this.$store.dispatch("fullscreenLoadingStop");
                  } else {
                    this.$store.dispatch("fullscreenLoadingStop");
                  }
                }.bind(this)
              )
              .catch(
                function () {
                  this.editDialog = false;
                  this.$store.dispatch("fullscreenLoadingStop");
                }.bind(this)
              );
          } else {
            this.$store.dispatch("fullscreenLoadingStop");
          }
        }.bind(this)
      );
    },
    del() {
      StockPosition.del(this.currentItem.id).then(
        function (response) {
          if (response.data.status.isOK()) {
            this.getPostionsFromAPI();
          }
        }.bind(this)
      );
    },
    importExcel() {
      this.$store.dispatch("fullscreenLoading");
      StockPosition.imports(this.excelFile).then(
        function (response) {
          if (response.data.status.isOK()) {
            if (response.data.data && response.data.data.length) {
              this.importDialog = false;
              this.importResultDialog = true;
              this.importResult = response.data.data;
            } else {
              this.$snackbar.info(i18n.t("Import.success", [i18n.t("Position.ObjectName")]));
              this.importDialog = false;
              this.getPostionsFromAPI();
            }
          }
          this.$store.dispatch("fullscreenLoadingStop");
        }.bind(this)
      ).catch(
        function () {
          this.$store.dispatch("fullscreenLoadingStop");
        }.bind(this)
      );
    },
  },

  watch: {
    options: {
      // immediate: true, //为避免覆盖session里保存的options，不能启用immediate
      deep: true,
      handler() {
        this.getPostionsFromAPI();
        sessionStorage.setItem(
          "positions-options",
          JSON.stringify(this.options)
        );
      },
    },
    positionLabelsSelected: {
      handler() {
        this.options.position_label_id = this.positionLabelsSelected
          ? this.positionLabelsSelected.id
          : "";
        sessionStorage.setItem(
          "positions-positionLabelsSelected",
          JSON.stringify(this.positionLabelsSelected)
        );
      },
    },
    stockAreasSelected: {
      handler() {
        this.options.stock_area_id = this.stockAreasSelected
          ? this.stockAreasSelected.id
          : "";
        sessionStorage.setItem(
          "positions-stockAreasSelected",
          JSON.stringify(this.stockAreasSelected)
        );
      },
    },
    // stocksSelected: {
    //   handler() {
    //     this.options.stock_id = this.stocksSelected
    //       ? this.stocksSelected.id
    //       : "";
    //     if (this.stocksSelected) {
    //       this.options.stock_id = this.stocksSelected.id;
    //       this.getStock(this.stocksSelected.id);
    //     } else {
    //       this.options.stock_id = "";
    //       this.getStock(0);
    //     }
    //     sessionStorage.setItem(
    //       "positions-stocksSelected",
    //       JSON.stringify(this.stocksSelected)
    //     );
    //   },
    // },
    editDialog: {
      handler() {
        if (!this.editDialog && Object.keys(this.currentItem).length) {
          this.currentItem = {};
          this.$refs.observer.reset();
          this.serverErrors = [];
        }
      },
    },
    currentPositionLabel: {
      handler() {
        if (this.currentPositionLabel && this.currentPositionLabel.id) {
          this.currentItem.position_label_id = this.currentPositionLabel.id;
        }
      }
    },
    currentItem: {
      deep: true,
      handler() {
        if (this.currentItem.position_label_id) {
          this.currentPositionLabel = {
            id: this.currentItem.position_label_id,
          };
        } else {
          this.currentPositionLabel = null;
        }
        if (this.currentItem.stock_area_id) {
          this.stockAreasBinding = {
            id: this.currentItem.stock_area_id,
          };
        } else {
          this.stockAreasBinding = null;
        }
        if (this.currentItem.roadway_id) {
          this.stockRoadwayBinding = {
            id: this.currentItem.roadway_id,
          };
        } else {
          this.stockRoadwayBinding = null;
        }
      },
    },
  },
};
</script>